import { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { apiFetch } from '../api/core'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PartyPreset {
  id: number
  label: string
  name: string
  gln: string
  iban: string | null
  vat: string | null
  kvk: string | null
  attn: string | null
  address: string | null
  postal: string | null
  city: string | null
  phone: string | null
  email: string | null
  website: string | null
  has_logo: boolean
  created_by: string
  created_at: string
}

interface LogoOut {
  has_logo: boolean
  logo_base64: string | null
}

export async function getPartyPresetLogo(id: number): Promise<LogoOut> {
  return apiFetch<LogoOut>(`/api/party-presets/${id}/logo`)
}

export async function savePartyPresetLogo(id: number, logoBase64: string): Promise<LogoOut> {
  return apiFetch<LogoOut>(`/api/party-presets/${id}/logo`, {
    method: 'PUT',
    body: JSON.stringify({ logo_base64: logoBase64 }),
  })
}

export async function deletePartyPresetLogo(id: number): Promise<void> {
  return apiFetch<void>(`/api/party-presets/${id}/logo`, { method: 'DELETE' })
}

interface PresetForm {
  label: string
  name: string
  gln: string
  iban: string
  vat: string
  kvk: string
  attn: string
  address: string
  postal: string
  city: string
  phone: string
  email: string
  website: string
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const inputCls = 'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors'

function emptyForm(): PresetForm {
  return { label: '', name: '', gln: '', iban: '', vat: '', kvk: '', attn: '', address: '', postal: '', city: '', phone: '', email: '', website: '' }
}

function presetToForm(p: PartyPreset): PresetForm {
  return {
    label: p.label, name: p.name, gln: p.gln,
    iban: p.iban ?? '', vat: p.vat ?? '', kvk: p.kvk ?? '', attn: p.attn ?? '',
    address: p.address ?? '', postal: p.postal ?? '', city: p.city ?? '',
    phone: p.phone ?? '', email: p.email ?? '', website: p.website ?? '',
  }
}

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

function PresetModal({
  initial, presetId, initialHasLogo, onSave, onSaved, onClose,
}: {
  initial: PresetForm
  presetId: number | null
  initialHasLogo: boolean
  onSave: (form: PresetForm) => Promise<PartyPreset>
  onSaved: () => Promise<void>
  onClose: () => void
}) {
  const { t } = useTranslation()
  const [form, setForm] = useState<PresetForm>(initial)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Logo per relatie: bestaand logo wordt ter preview opgehaald, een nieuwe
  // upload of verwijdering wordt pas doorgevoerd nadat de partij zelf is
  // opgeslagen (bij een nieuwe partij bestaat het id pas na de POST).
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [logoLoading, setLogoLoading] = useState(initialHasLogo)
  const [pendingLogoBase64, setPendingLogoBase64] = useState<string | null>(null)
  const [removeLogo, setRemoveLogo] = useState(false)
  const logoInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!presetId || !initialHasLogo) { setLogoLoading(false); return }
    getPartyPresetLogo(presetId)
      .then(res => { if (res.has_logo && res.logo_base64) setLogoPreview(res.logo_base64) })
      .catch(() => { /* niet kritisch */ })
      .finally(() => setLogoLoading(false))
  }, [presetId, initialHasLogo])

  const set = (key: keyof PresetForm) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(f => ({ ...f, [key]: e.target.value }))

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = ev => {
      const result = ev.target?.result as string
      const base64 = result.split(',')[1]
      setPendingLogoBase64(base64)
      setLogoPreview(base64)
      setRemoveLogo(false)
    }
    reader.readAsDataURL(file)
  }

  const handleLogoRemove = () => {
    setPendingLogoBase64(null)
    setLogoPreview(null)
    setRemoveLogo(true)
    if (logoInputRef.current) logoInputRef.current.value = ''
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.label.trim()) { setError(t('party_presets_page.error_label_required')); return }
    if (!form.name.trim())  { setError(t('party_presets_page.error_name_required')); return }
    if (!form.gln.trim())   { setError(t('party_presets_page.error_gln_required')); return }
    setSaving(true)
    setError(null)
    try {
      const saved = await onSave(form)
      if (pendingLogoBase64) {
        await savePartyPresetLogo(saved.id, pendingLogoBase64)
      } else if (removeLogo) {
        await deletePartyPresetLogo(saved.id)
      }
      await onSaved()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('party_presets_page.error_save_failed'))
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            {initial.label ? t('party_presets_page.modal_title_edit') : t('party_presets_page.modal_title_new')}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl leading-none">&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 rounded-lg px-4 py-2.5 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">{t('party_presets_page.field_label_display_name')}</label>
            <input className={inputCls} value={form.label} onChange={set('label')} placeholder={t('party_presets_page.placeholder_label')} autoFocus />
            <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">{t('party_presets_page.hint_label')}</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">{t('party_presets_page.field_label_company_name')}</label>
              <input className={inputCls} value={form.name} onChange={set('name')} placeholder={t('party_presets_page.placeholder_name')} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">{t('party_presets_page.field_label_gln')}</label>
              <input className={inputCls} value={form.gln} onChange={set('gln')} placeholder={t('party_presets_page.placeholder_gln')} required minLength={1} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">{t('party_presets_page.field_label_attn')}</label>
              <input className={inputCls} value={form.attn} onChange={set('attn')} placeholder={t('party_presets_page.placeholder_attn')} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">{t('party_presets_page.field_label_vat')}</label>
              <input className={inputCls} value={form.vat} onChange={set('vat')} placeholder={t('party_presets_page.placeholder_vat')} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">{t('party_presets_page.field_label_address')}</label>
              <input className={inputCls} value={form.address} onChange={set('address')} placeholder={t('party_presets_page.placeholder_address')} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">{t('party_presets_page.field_label_postal')}</label>
              <input className={inputCls} value={form.postal} onChange={set('postal')} placeholder={t('party_presets_page.placeholder_postal')} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">{t('party_presets_page.field_label_city')}</label>
              <input className={inputCls} value={form.city} onChange={set('city')} placeholder={t('party_presets_page.placeholder_city')} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">{t('party_presets_page.field_label_iban')}</label>
              <input className={inputCls} value={form.iban} onChange={set('iban')} placeholder={t('party_presets_page.placeholder_iban')} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">{t('party_presets_page.field_label_kvk')}</label>
              <input className={inputCls} value={form.kvk} onChange={set('kvk')} placeholder={t('party_presets_page.placeholder_kvk')} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">{t('party_presets_page.field_label_phone')}</label>
              <input className={inputCls} value={form.phone} onChange={set('phone')} placeholder={t('party_presets_page.placeholder_phone')} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">{t('party_presets_page.field_label_email')}</label>
              <input className={inputCls} value={form.email} onChange={set('email')} placeholder={t('party_presets_page.placeholder_email')} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">{t('party_presets_page.field_label_website')}</label>
              <input className={inputCls} value={form.website} onChange={set('website')} placeholder={t('party_presets_page.placeholder_website')} />
            </div>
          </div>

          <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">{t('party_presets_page.logo_label')}</label>
            <p className="text-xs text-gray-400 dark:text-gray-500 mb-2">{t('party_presets_page.logo_hint')}</p>
            {logoLoading ? (
              <span className="text-xs text-gray-400">{t('party_presets_page.loading')}</span>
            ) : logoPreview ? (
              <div className="flex items-center gap-3 flex-wrap">
                <img src={`data:image/png;base64,${logoPreview}`} alt="" className="h-10 max-w-[8rem] object-contain rounded border border-gray-200 dark:border-gray-600 bg-white" />
                <label className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 cursor-pointer transition-colors">
                  {t('party_presets_page.logo_replace')}
                  <input ref={logoInputRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={handleLogoUpload} />
                </label>
                <button type="button" onClick={handleLogoRemove} className="text-xs text-red-500 hover:text-red-700 transition-colors">
                  {t('party_presets_page.logo_remove')}
                </button>
              </div>
            ) : (
              <label className="inline-flex items-center gap-2 cursor-pointer text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 border border-blue-200 dark:border-blue-700 rounded-lg px-3 py-1.5 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {t('party_presets_page.logo_upload')}
                <input ref={logoInputRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={handleLogoUpload} />
              </label>
            )}
          </div>
        </form>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg transition-colors"
          >
            {t('party_presets_page.button_cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg transition-colors"
          >
            {saving ? t('party_presets_page.button_saving') : t('party_presets_page.button_save')}
          </button>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function PartyPresetsPage() {
  const { t } = useTranslation()
  const [presets, setPresets] = useState<PartyPreset[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [modal, setModal] = useState<{ form: PresetForm; id: number | null; hasLogo: boolean } | null>(null)
  const [deletingId, setDeletingId] = useState<number | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await apiFetch<PartyPreset[]>('/api/party-presets')
      setPresets(data)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('party_presets_page.error_load_failed'))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => { load() }, [load])

  const openNew = () => setModal({ form: emptyForm(), id: null, hasLogo: false })
  const openEdit = (p: PartyPreset) => setModal({ form: presetToForm(p), id: p.id, hasLogo: p.has_logo })

  const handleSave = async (form: PresetForm): Promise<PartyPreset> =>
    modal!.id
      ? apiFetch<PartyPreset>(`/api/party-presets/${modal!.id}`, { method: 'PUT', body: JSON.stringify(form) })
      : apiFetch<PartyPreset>('/api/party-presets', { method: 'POST', body: JSON.stringify(form) })

  const handleSaved = async () => {
    setModal(null)
    await load()
  }

  const handleDelete = async (id: number) => {
    if (!confirm(t('party_presets_page.confirm_delete'))) return
    setDeletingId(id)
    try {
      await apiFetch(`/api/party-presets/${id}`, { method: 'DELETE' })
      await load()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('party_presets_page.error_delete_failed'))
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div className="space-y-5">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 rounded-xl px-4 py-3 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-4 text-red-400 hover:text-red-600">&times;</button>
        </div>
      )}

      <div className="flex items-center justify-end">
        <button
          onClick={openNew}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          {t('party_presets_page.button_add')}
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {loading ? (
          <div className="py-12 text-center text-sm text-gray-400">{t('party_presets_page.loading')}</div>
        ) : presets.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('party_presets_page.empty_title')}</p>
            <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
              {t('party_presets_page.empty_hint')}
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{t('party_presets_page.col_label')}</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{t('party_presets_page.col_name')}</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{t('party_presets_page.col_gln')}</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{t('party_presets_page.col_city')}</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{t('party_presets_page.col_logo')}</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {presets.map(p => (
                <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                  <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">{p.label}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{p.name}</td>
                  <td className="px-4 py-3 text-gray-500 dark:text-gray-400 font-mono text-xs">{p.gln}</td>
                  <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{p.city ?? '—'}</td>
                  <td className="px-4 py-3">
                    {p.has_logo && (
                      <span className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400">
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {t('party_presets_page.logo_badge')}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => openEdit(p)}
                        className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 transition-colors"
                      >
                        {t('party_presets_page.button_edit')}
                      </button>
                      <button
                        onClick={() => handleDelete(p.id)}
                        disabled={deletingId === p.id}
                        className="text-xs text-red-500 hover:text-red-700 dark:hover:text-red-300 disabled:opacity-40 transition-colors"
                      >
                        {deletingId === p.id ? t('party_presets_page.deleting') : t('party_presets_page.button_delete')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <PresetModal
          initial={modal.form}
          presetId={modal.id}
          initialHasLogo={modal.hasLogo}
          onSave={handleSave}
          onSaved={handleSaved}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  )
}
